/**
 * Agents & groups — Step 163.
 *
 * Kai: *"/admin/groups is just groups and an awful UI at that… create a new
 * agents & groups page."* Reading the code for it found the larger gaps this
 * page closes: there was no way to create an agent, and no way to give anyone
 * a role — so Step 99's matrix was unreachable end to end.
 *
 * Two tabs in the URL (`?tab=agents|groups`), one selected agent in the URL
 * (`?agent=<id>`) — every view has a URL. The agent editor is draft-first for
 * the free text and the pickers, with ONE atomic act outside it: Deactivate.
 * Ending somebody's access is not a field on a form you might forget to save;
 * it is a decision, confirmed, and it takes effect within seconds because the
 * session now re-checks itself (see `principalFreshness.ts`).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  KeyRound,
  Mail,
  Plus,
  Search,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Modal } from '../../components/ui/modal';
import { TokenSelect } from '../../components/ui/tokenselect';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import { useAuth } from '../../lib/AuthContext';
import { relativeTime } from '../../lib/time';
import { cn } from '../../lib/utils';
import { GroupsSection } from './GroupsPage';

interface Named {
  id: string;
  name: string;
}
interface Agent {
  id: string;
  name: string | null;
  email: string;
  kind: 'agent' | 'platform';
  isActive: boolean;
  /** Step 145 — "Available for auto-assigning tickets". */
  availableForAssignment: boolean;
  jobTitle: string | null;
  department: string | null;
  lastLoginAt: number | null;
  signedInWith: 'microsoft' | 'link' | null;
  roles: (Named & { key: string })[];
  groups: Named[];
}
interface Payload {
  entries: Agent[];
  roles: (Named & { key: string })[];
  groups: Named[];
}

export function AgentsPage() {
  const [tabRaw, setTab] = useUrlState('tab', 'agents');
  const tab = tabRaw === 'groups' ? 'groups' : 'agents';

  return (
    <div className="p-6">
      <div className="mb-3 flex items-center gap-3">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Agents &amp; groups</h1>
      </div>
      <div className="mb-5 flex gap-1 border-b">
        {(['agents', 'groups'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              tab === t
                ? 'border-brand text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'agents' ? <AgentsTab /> : <GroupsSection />}
    </div>
  );
}

function AgentsTab() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [selectedId, setSelectedId] = useUrlState('agent', null);
  const [inviting, setInviting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  // In the URL like every other filter: "the agents in Networks" is a link
  // somebody sends, not a state they rebuild.
  const [q, setQ] = useUrlState('q', '');

  const loaded = useQuery({
    queryKey: qk.adminAgents,
    queryFn: () => apiFetch<Payload>('/api/admin/agents'),
  });

  if (loaded.isError) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm font-medium">Agents could not be loaded.</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (loaded.isPending) return <Skeleton className="h-64 w-full" />;

  const all = loaded.data.entries;
  const needle = (q ?? '').trim().toLowerCase();
  const shown = all.filter(
    (a) =>
      (showInactive || a.isActive) &&
      (!needle ||
        [a.name, a.email, a.jobTitle, ...a.roles.map((r) => r.name), ...a.groups.map((g) => g.name)]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(needle))),
  );
  const inactiveCount = all.filter((a) => !a.isActive).length;
  const selected = all.find((a) => a.id === selectedId) ?? null;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr,22rem]">
      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-8 w-64 pl-7 text-sm"
              placeholder="Search name, email, role, group…"
              value={q ?? ''}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {shown.length} {shown.length === 1 ? 'agent' : 'agents'}
            {needle && all.length !== shown.length && ` of ${all.length}`}
          </p>
          {inactiveCount > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-input"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show {inactiveCount} deactivated
            </label>
          )}
          <div className="flex-1" />
          <Button size="sm" onClick={() => setInviting(true)}>
            <UserPlus /> Add agent
          </Button>
        </div>

        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-muted/40 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Agent</th>
                <th className="px-3 py-2 text-left font-medium">Roles</th>
                <th className="px-3 py-2 text-left font-medium">Groups</th>
                <th className="px-3 py-2 text-left font-medium">Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  className={cn(
                    'cursor-pointer border-t hover:bg-accent/40',
                    selectedId === a.id && 'bg-brand/5',
                    !a.isActive && 'opacity-60',
                  )}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {(a.name ?? a.email).slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {a.name ?? a.email}
                          {a.id === me?.user.id && (
                            <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>
                          )}
                          {!a.isActive && (
                            <Badge variant="secondary" className="ml-1.5">
                              Deactivated
                            </Badge>
                          )}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {a.email}
                          {a.jobTitle && ` · ${a.jobTitle}`}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.kind === 'platform' && <Badge>Platform admin</Badge>}
                      {a.roles.map((r) => (
                        <Badge key={r.id} variant="secondary">
                          {r.name}
                        </Badge>
                      ))}
                      {a.kind !== 'platform' && a.roles.length === 0 && (
                        // Worth saying loudly: no roles means no access, and it
                        // is the state a freshly-added agent is in if the
                        // picker was skipped.
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          No roles — cannot do anything yet
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.groups.map((g) => (
                        <Badge key={g.id} variant="outline">
                          {g.name}
                        </Badge>
                      ))}
                      {a.groups.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {a.lastLoginAt ? relativeTime(a.lastLoginAt) : 'Never'}
                    {a.signedInWith === 'microsoft' && <span className="ml-1">· Microsoft</span>}
                    {a.signedInWith === 'link' && <span className="ml-1">· email link</span>}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    Nobody here yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        {selected ? (
          <AgentEditor
            key={selected.id}
            agent={selected}
            roles={loaded.data.roles}
            groups={loaded.data.groups}
            isSelf={selected.id === me?.user.id}
            onClose={() => setSelectedId(null)}
          />
        ) : (
          <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Pick an agent to see and change what they can do.
          </p>
        )}
      </div>

      {inviting && (
        <InviteModal
          roles={loaded.data.roles}
          groups={loaded.data.groups}
          onClose={() => setInviting(false)}
          onCreated={(id) => {
            setInviting(false);
            void qc.invalidateQueries({ queryKey: qk.adminAgents });
            setSelectedId(id);
          }}
        />
      )}
    </div>
  );
}

function PickList({
  label,
  options,
  value,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  options: Named[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  hint?: string;
}) {
  /**
   * A searchable token field, not a row of chips. Kai: *"what if we have 50
   * groups in a large org? same with roles."* Chips are fine for six options
   * and a wall for fifty; a field you type into is the same size at any count.
   * `TokenSelect` is the house primitive for exactly this.
   */
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      {disabled ? (
        <div className="flex flex-wrap gap-1">
          {options
            .filter((o) => value.includes(o.id))
            .map((o) => (
              <Badge key={o.id} variant="secondary">
                {o.name}
              </Badge>
            ))}
          {value.length === 0 && <span className="text-xs text-muted-foreground">None</span>}
        </div>
      ) : (
        <TokenSelect
          value={value}
          options={options.map((o) => ({ value: o.id, label: o.name }))}
          onChange={onChange}
          placeholder={options.length ? `Search ${label.toLowerCase()}…` : 'None defined yet'}
          ariaLabel={label}
        />
      )}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function AgentEditor({
  agent,
  roles,
  groups,
  isSelf,
  onClose,
}: {
  agent: Agent;
  roles: Named[];
  groups: Named[];
  isSelf: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const stored = {
    name: agent.name ?? '',
    jobTitle: agent.jobTitle ?? '',
    roleIds: agent.roles.map((r) => r.id).sort(),
    groupIds: agent.groups.map((g) => g.id).sort(),
  };
  const [draft, setDraft] = useState(stored);
  const [error, setError] = useState<string | null>(null);
  const dirty =
    JSON.stringify({ ...draft, roleIds: [...draft.roleIds].sort(), groupIds: [...draft.groupIds].sort() }) !==
    JSON.stringify(stored);
  useDirty(dirty);
  const platform = agent.kind === 'platform';

  const save = useMutation({
    mutationFn: () =>
      apiSend(`/api/admin/agents/${agent.id}`, 'PATCH', {
        name: draft.name.trim(),
        jobTitle: draft.jobTitle.trim() || null,
        ...(platform ? {} : { roleIds: draft.roleIds }),
        groupIds: draft.groupIds,
      }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be saved.');
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminAgents });
      void qc.invalidateQueries({ queryKey: qk.adminGroups });
      // Applies within seconds — see principalFreshness. Said so, because the
      // old behaviour (next sign-in) is what people expect and would wait for.
      toast.success('Saved. Changes apply to their session within about 30 seconds.');
    },
  });

  // Step 145 — atomic like Deactivate: stepping out of the round robin is a
  // switch, not a form.
  const toggleAvailable = useMutation({
    mutationFn: (availableForAssignment: boolean) =>
      apiSend(`/api/admin/agents/${agent.id}`, 'PATCH', { availableForAssignment }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      void qc.invalidateQueries({ queryKey: qk.adminAgents });
    },
  });
  const toggleActive = useMutation({
    mutationFn: (isActive: boolean) => apiSend(`/api/admin/agents/${agent.id}`, 'PATCH', { isActive }),
    onSuccess: (res, isActive) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      void qc.invalidateQueries({ queryKey: qk.adminAgents });
      toast.success(
        isActive ? 'Access restored.' : 'Access ended. They will be signed out within about 30 seconds.',
      );
    },
  });

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-start gap-3 border-b p-4">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{agent.name ?? agent.email}</p>
          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            <Mail className="h-3 w-3" /> {agent.email}
          </p>
        </div>
        <Link
          to={`/people/${agent.id}`}
          className="rounded text-xs text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ExternalLink className="mr-0.5 inline h-3 w-3" /> Full profile
        </Link>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Job title</label>
          <Input
            value={draft.jobTitle}
            placeholder="Service Desk Analyst"
            onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })}
          />
        </div>
        {platform ? (
          <p className="flex items-start gap-1.5 rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
            <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            The platform account holds every permission in code. It cannot be re-roled or deactivated here —
            it is the way back in if a permission is ever set wrong.
          </p>
        ) : (
          <PickList
            label="Roles"
            options={roles}
            value={draft.roleIds}
            onChange={(roleIds) => setDraft({ ...draft, roleIds })}
            hint="Roles add up: somebody with two can do everything either allows."
          />
        )}
        <PickList
          label="Groups"
          options={groups}
          value={draft.groupIds}
          onChange={(groupIds) => setDraft({ ...draft, groupIds })}
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-3">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => setDraft(stored)}
          >
            Discard
          </Button>
          <Button
            size="sm"
            disabled={!dirty || save.isPending || !draft.name.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <label className="flex items-start gap-2 border-t p-4 text-sm">
        {/* Auto-commit on purpose: a switch beside Deactivate, the same atomic act — stepping out of the round robin is not a form. */}
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-input"
          checked={agent.availableForAssignment}
          disabled={toggleAvailable.isPending}
          onChange={(e) => toggleAvailable.mutate(e.target.checked)}
        />
        <span>
          Available for auto-assigning tickets
          <span className="block text-xs text-muted-foreground">
            Off keeps them in their groups and their queues, but the round robin skips them — leave, training,
            a day on the phones.
          </span>
        </span>
      </label>
      {/* Deactivate is OUTSIDE the draft on purpose: ending access is a
          decision, not a field you might forget to save. */}
      {!platform && (
        <div className="border-t p-4">
          {agent.isActive ? (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              disabled={isSelf || toggleActive.isPending}
              title={isSelf ? 'You cannot deactivate your own account.' : undefined}
              onClick={async () => {
                if (
                  await confirm({
                    title: `End ${agent.name ?? agent.email}'s access?`,
                    body: 'They are signed out within about 30 seconds and cannot sign back in. Their tickets and history are kept. You can restore access later.',
                    confirmLabel: 'Deactivate',
                    destructive: true,
                  })
                )
                  toggleActive.mutate(false);
              }}
            >
              <UserMinus /> Deactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleActive.isPending}
              onClick={() => toggleActive.mutate(true)}
            >
              <ShieldCheck /> Restore access
            </Button>
          )}
        </div>
      )}
      <div className="border-t px-4 py-2 text-right">
        <button onClick={onClose} className="text-xs text-muted-foreground hover:underline">
          Close
        </button>
      </div>
    </div>
  );
}

function InviteModal({
  roles,
  groups,
  onClose,
  onCreated,
}: {
  roles: (Named & { key: string })[];
  groups: Named[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  // Default to the Agent role: an invite with no role produces somebody who
  // can sign in and do nothing, which reads as broken rather than as a choice.
  const agentRole = roles.find((r) => r.key === 'agent');
  const [form, setForm] = useState({
    email: '',
    name: '',
    jobTitle: '',
    roleIds: agentRole ? [agentRole.id] : ([] as string[]),
    groupIds: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      apiSend<{ id: string; promoted: boolean }>('/api/admin/agents', 'POST', {
        ...form,
        jobTitle: form.jobTitle.trim() || undefined,
      }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That did not work.');
      toast.success(
        res.body?.promoted
          ? 'They were already a requester here — promoted to agent, history kept.'
          : 'Agent added and emailed a sign-in link to this page.',
      );
      if (res.body?.id) onCreated(res.body.id);
    },
  });
  return (
    <Modal open onClose={onClose} title="Add an agent">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <p className="text-sm text-muted-foreground">
          They sign in with the email below — with Microsoft if your organisation uses it, or by asking for a
          link on the sign-in page. Nobody gets in without being added here.
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Work email</label>
          <Input
            type="email"
            required
            autoFocus
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="alex@yourcompany.com"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Job title (optional)</label>
          <Input value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
        </div>
        <PickList
          label="Roles"
          options={roles}
          value={form.roleIds}
          onChange={(roleIds) => setForm({ ...form, roleIds })}
        />
        <PickList
          label="Groups"
          options={groups}
          value={form.groupIds}
          onChange={(groupIds) => setForm({ ...form, groupIds })}
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || !form.email.trim() || !form.name.trim()}>
            <Plus /> {create.isPending ? 'Adding…' : 'Add agent'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
